/**
 * お問い合わせ（ご意見・ご要望）— 管理画面のどこからでも開ける送信ダイアログと、運営とのやりとりの画面（2026-10-05）。
 *
 * 簡単に送れることを優先する: 担当者・会社名・開いている画面は自動で付き、書くのは種類と本文だけ。
 * 商品の画面から開くと、その商品が「対象」として付く（外すこともできる）。
 * 運営の返信は**この管理画面の中**に届き（メールでは往復しない・`src/routes/feedback.ts`）、画面上部の「お問い合わせ」に
 * 未読の数が出る。メールは「返信があります」の知らせだけ。
 *
 * ⚠️ ダイアログはネイティブの `<dialog>`（showModal）— Esc で閉じる・フォーカスの閉じ込め・背景の不活性化をブラウザが持つ。
 */
import { createContext, useCallback, useContext, useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import { BRAND_FEEDBACK_CATEGORIES, BRAND_FEEDBACK_MAX_LENGTH, type BrandFeedbackCategory } from "@kalori/business/brands";
import { api, type FeedbackMessage, type FeedbackThread } from "../api";
import { Link, navigate } from "../router";
import { FEEDBACK_STATUS_LABEL, fmtDateTime } from "../format";
import { StateChip } from "./parts";

export interface FeedbackTarget {
  category?: BrandFeedbackCategory;
  product?: { id: number; name: string };
}

interface FeedbackApi {
  open: (target?: FeedbackTarget) => void;
  /** 運営の返信をまだ開いていないお問い合わせの数 */
  unread: number;
  /** スレッドを開いて未読が消えたとき */
  markRead: () => void;
}

const FeedbackContext = createContext<FeedbackApi>({ open: () => {}, unread: 0, markRead: () => {} });

/** 任意の画面から `const { open } = useFeedback(); open({ product })` で開く。 */
export function useFeedback() {
  return useContext(FeedbackContext);
}

const categoryLabel = (key: string) => BRAND_FEEDBACK_CATEGORIES[key as BrandFeedbackCategory] ?? key;

/** 種類ごとの書き出しの例（何を書けばよいかで手が止まらないように）。 */
const PLACEHOLDER: Record<BrandFeedbackCategory, string> = {
  listing: "例: このメニューは 10 月末で終売になります／いまの価格は 990 円です",
  feature: "例: 変更があったメニューを知らせてほしい／まとめて入力したい",
  trouble: "例: 申請ボタンを押しても先に進まない",
  question: "例: 季節限定のメニューはどう扱えばよいですか",
  other: "",
};

export function FeedbackProvider({ initialUnread, children }: { initialUnread: number; children: ReactNode }) {
  const dialog = useRef<HTMLDialogElement>(null);
  const [unread, setUnread] = useState(initialUnread);
  const [category, setCategory] = useState<BrandFeedbackCategory | null>(null);
  const [product, setProduct] = useState<FeedbackTarget["product"] | null>(null);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createdId, setCreatedId] = useState<string | null>(null);

  const open = useCallback((target: FeedbackTarget = {}) => {
    setCategory(target.category ?? null);
    setProduct(target.product ?? null);
    setMessage("");
    setError(null);
    setCreatedId(null);
    dialog.current?.showModal();
  }, []);
  const markRead = useCallback(() => setUnread((n) => Math.max(0, n - 1)), []);
  const close = () => dialog.current?.close();

  // 画面を移ったら閉じる（別の画面の話として送られないように）
  useEffect(() => {
    const onPop = () => dialog.current?.close();
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!category || !message.trim()) return;
    setBusy(true);
    setError(null);
    const r = await api.createFeedback({
      category,
      message: message.trim(),
      page: `${window.location.pathname}${window.location.search}`,
      productDbId: product?.id ?? null,
    });
    setBusy(false);
    if (r.ok) setCreatedId(r.data.id);
    else setError(r.error);
  };

  return (
    <FeedbackContext.Provider value={{ open, unread, markRead }}>
      {children}
      <dialog ref={dialog} className="modal" aria-labelledby="feedback-title">
        {createdId ? (
          <div className="stack">
            <h2 id="feedback-title">送信しました</h2>
            <p>ありがとうございます。運営からの返信は、この管理画面の「お問い合わせ」に届きます（届いたらメールでもお知らせします）。</p>
            <div className="actions">
              <span className="spacer" />
              <button type="button" className="btn" onClick={close}>
                閉じる
              </button>
              <button
                type="button"
                className="btn primary"
                onClick={() => {
                  close();
                  navigate(`/feedback/${createdId}`);
                }}
              >
                やりとりを開く
              </button>
            </div>
          </div>
        ) : (
          <form className="stack" onSubmit={(e) => void submit(e)}>
            <div className="row-between">
              <h2 id="feedback-title">ご意見・ご要望</h2>
              <button type="button" className="btn-link" onClick={close}>
                閉じる
              </button>
            </div>
            <p className="muted small">
              kalori の運営に直接届き、返信はこの管理画面の「お問い合わせ」に届きます。ほしい機能のご要望は、これから足す機能の優先順位の決め手にしています。
            </p>
            {product && (
              <p className="target">
                対象: <strong>{product.name}</strong>{" "}
                <button type="button" className="btn-link small" onClick={() => setProduct(null)}>
                  外す
                </button>
              </p>
            )}
            <fieldset className="choices">
              <legend>何についてですか</legend>
              {(Object.keys(BRAND_FEEDBACK_CATEGORIES) as BrandFeedbackCategory[]).map((key) => (
                <label key={key} className={category === key ? "choice selected" : "choice"}>
                  <input type="radio" name="category" value={key} checked={category === key} onChange={() => setCategory(key)} />
                  {BRAND_FEEDBACK_CATEGORIES[key]}
                </label>
              ))}
            </fieldset>
            <label className="field">
              <span>内容</span>
              <textarea
                rows={6}
                maxLength={BRAND_FEEDBACK_MAX_LENGTH}
                value={message}
                placeholder={category ? PLACEHOLDER[category] : ""}
                onChange={(e) => setMessage(e.target.value)}
                required
              />
            </label>
            {error && <p className="error">{error}</p>}
            <div className="actions">
              <span className="muted small">
                {message.length.toLocaleString("ja-JP")} / {BRAND_FEEDBACK_MAX_LENGTH.toLocaleString("ja-JP")}
              </span>
              <span className="spacer" />
              <button type="button" className="btn" onClick={close}>
                やめる
              </button>
              <button type="submit" className="btn primary" disabled={busy || !category || !message.trim()}>
                {busy ? "送信しています…" : "送信する"}
              </button>
            </div>
          </form>
        )}
      </dialog>
    </FeedbackContext.Provider>
  );
}

/** お問い合わせの一覧（/feedback）— 新しい動きの順。運営の返信を開いていないものに印。 */
export function FeedbackList() {
  const { open } = useFeedback();
  const [threads, setThreads] = useState<FeedbackThread[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void api.feedbackList().then((r) => (r.ok ? setThreads(r.data) : setError(r.error)));
  }, []);

  return (
    <section className="stack-lg">
      <div className="row-between">
        <div>
          <h1>お問い合わせ</h1>
          <p className="muted">運営とのやりとりです。返信もこの画面からお送りいただけます。</p>
        </div>
        <button className="btn primary" onClick={() => open()}>
          新しく送る
        </button>
      </div>
      {error && <p className="error">{error}</p>}
      {!error && threads === null && <p className="muted">読み込んでいます…</p>}
      {threads && threads.length === 0 && (
        <div className="card">
          <p>まだお問い合わせはありません。掲載内容の誤り・ほしい機能・使いにくいところなど、何でもお送りください。</p>
        </div>
      )}
      {threads && threads.length > 0 && (
        <div className="card flush">
          <ul className="list">
            {threads.map((t) => (
              <li key={t.id} className={t.unread ? "list-row unread" : "list-row"}>
                <span className="thread-summary">
                  <Link href={`/feedback/${t.id}`} className="row-title">
                    {t.unread && <span className="dot" aria-label="未読" />}
                    {categoryLabel(t.category)}
                    {t.productName && <span className="muted"> ・ {t.productName}</span>}
                  </Link>
                  {t.lastMessage && (
                    <span className="muted small preview">
                      {t.lastMessage.sender === "operator" ? "運営: " : ""}
                      {t.lastMessage.body}
                    </span>
                  )}
                </span>
                <span className="chips">
                  <span className="muted small">{fmtDateTime(t.updatedAt)}</span>
                  <StateChip state={`feedback-${t.status}`} label={FEEDBACK_STATUS_LABEL[t.status]} />
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

/** 1 つのお問い合わせのやりとり（/feedback/:id）。開いたら運営の返信を既読にする。 */
export function FeedbackThreadView({ id }: { id: string }) {
  const { markRead } = useFeedback();
  const [thread, setThread] = useState<(FeedbackThread & { messages: FeedbackMessage[] }) | null | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const r = await api.feedbackThread(id);
    if (!r.ok) {
      setThread(null);
      return setError(r.error);
    }
    setThread(r.data);
    const last = r.data.messages.at(-1);
    if (r.data.unread && last) {
      // 画面に出した最後の発言までを既読にする（「いま」ではない — 読み込んだ後に届いた返信を読んだことにしない）。
      // 既読にできたときだけバッジを減らす（失敗しても次に開けばまた未読として出る）
      const read = await api.readFeedback(id, last.id);
      if (read.ok) markRead();
    }
  }, [id, markRead]);

  useEffect(() => {
    void load();
  }, [load]);

  const send = async (e: FormEvent) => {
    e.preventDefault();
    if (!reply.trim()) return;
    setBusy(true);
    setError(null);
    const r = await api.replyFeedback(id, reply.trim());
    setBusy(false);
    if (!r.ok) return setError(r.error);
    setReply("");
    await load();
  };

  if (thread === undefined) return <p className="muted">読み込んでいます…</p>;
  if (thread === null)
    return (
      <section className="stack">
        <p className="error">{error ?? "お問い合わせが見つかりません。"}</p>
        <Link href="/feedback">お問い合わせへ戻る</Link>
      </section>
    );

  return (
    <section className="stack-lg">
      <div>
        <p className="small">
          <Link href="/feedback">← お問い合わせ</Link>
        </p>
        <div className="row-between">
          <h1>{categoryLabel(thread.category)}</h1>
          <StateChip state={`feedback-${thread.status}`} label={FEEDBACK_STATUS_LABEL[thread.status]} />
        </div>
        <p className="muted small">
          {thread.productName &&
            (thread.productDbId ? (
              <>
                対象: <Link href={`/catalog/${thread.productDbId}`}>{thread.productName}</Link> ・{" "}
              </>
            ) : (
              <>対象: {thread.productName} ・ </>
            ))}
          送信 {fmtDateTime(thread.createdAt)}
        </p>
      </div>

      <ol className="thread">
        {thread.messages.map((m) => (
          <li key={m.id} className={m.sender === "operator" ? "message operator" : "message"}>
            <div className="message-head">
              <strong>{m.author}</strong> <span className="muted small">{fmtDateTime(m.createdAt)}</span>
            </div>
            <div className="pre">{m.body}</div>
          </li>
        ))}
      </ol>

      <form className="card stack" onSubmit={(e) => void send(e)}>
        <label className="field">
          <span>返信する</span>
          <textarea rows={4} maxLength={BRAND_FEEDBACK_MAX_LENGTH} value={reply} onChange={(e) => setReply(e.target.value)} required />
        </label>
        {thread.status === "closed" && <p className="muted small">完了したお問い合わせです。続けて書くと、運営の確認中に戻ります。</p>}
        {error && <p className="error">{error}</p>}
        <div className="actions">
          <span className="spacer" />
          <button type="submit" className="btn primary" disabled={busy || !reply.trim()}>
            {busy ? "送信しています…" : "送信する"}
          </button>
        </div>
      </form>
    </section>
  );
}
